import { useEffect, useState } from 'react';
import { IndexedDbStudyRepository } from '@hoedokloop/storage-indexeddb';
import type { Instant } from '@hoedokloop/domain';
import { usePlatform } from '../../platform/platform-context';
export function useLocalSetting<T>(key: string, fallback: T) {
  const { studyRepository } = usePlatform();
  const [value, setValue] = useState<T>(fallback);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => {
    let active = true;
    setReady(false);
    if (!(studyRepository instanceof IndexedDbStudyRepository)) {
      setError('이 기기의 설정 저장소를 열지 못했어요.');
      return;
    }
    void studyRepository.database.settings
      .get(key)
      .then((record) => {
        if (active) {
          setValue(record ? (record.value as T) : fallback);
          setReady(true);
        }
      })
      .catch(() => {
        if (active) setError('저장된 설정을 열지 못했어요. 다시 시도해 주세요.');
      });
    return () => {
      active = false;
    };
  }, [key, studyRepository]);
  const save = async (next: T): Promise<boolean> => {
    if (!(studyRepository instanceof IndexedDbStudyRepository)) return false;
    try {
      await studyRepository.database.settings.put({
        key,
        value: next,
        updatedAt: new Date().toISOString() as Instant,
      });
      setValue(next);
      setError(undefined);
      return true;
    } catch {
      setError('저장하지 못했어요. 입력은 그대로예요. 다시 시도해 주세요.');
      return false;
    }
  };
  const remove = async () => {
    if (!(studyRepository instanceof IndexedDbStudyRepository)) return false;
    try {
      await studyRepository.database.settings.delete(key);
      setValue(fallback);
      return true;
    } catch {
      setError('지우지 못했어요. 다시 시도해 주세요.');
      return false;
    }
  };
  return { value, save, remove, ready, error };
}
